export type MembershipDiscountSource = 'base' | 'special' | 'period' | 'none';

export interface MembershipPeriodOffer {
  discountPercent: number;
  startDate: Date | string;
  endDate: Date | string;
}

export interface SpecialOfferProductConfig {
  discountPercent?: number;
  periodOffers?: MembershipPeriodOffer[];
}

export interface MembershipDiscountInput {
  _id?: unknown;
  alias?: string;
  name?: string;
  discountPercent?: number;
  qualified?: boolean;
  benefits?: {
    allProducts?: boolean;
    specialOfferProducts?: { [productId: string]: SpecialOfferProductConfig };
  };
}

export interface MembershipDiscountResolution {
  percent: number;
  source: MembershipDiscountSource;
  basePercent: number;
  specialPercent?: number;
  period?: MembershipPeriodOffer;
}

export interface MembershipDiscountDetail {
  percent: number;
  source: MembershipDiscountSource;
  membershipId?: string;
  alias?: string;
  name?: string;
  basePercent?: number;
  specialPercent?: number;
  period?: { discountPercent: number; startDate: Date | string; endDate: Date | string };
}

function toDate(value: Date | string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function isPeriodOfferActive(offer: MembershipPeriodOffer, now: Date = new Date()): boolean {
  const start = toDate(offer.startDate);
  const end = toDate(offer.endDate);
  if (!start || !end) {
    return false;
  }
  return start.getTime() <= now.getTime() && now.getTime() < end.getTime();
}

export function periodOffersOverlap(offers: MembershipPeriodOffer[] = []): boolean {
  const ranges = offers
    .map((offer) => {
      const start = toDate(offer.startDate);
      const end = toDate(offer.endDate);
      if (!start || !end || start.getTime() >= end.getTime()) {
        return null;
      }
      return { start: start.getTime(), end: end.getTime() };
    })
    .filter((range): range is { start: number; end: number } => !!range)
    .sort((a, b) => a.start - b.start);

  for (let i = 1; i < ranges.length; i += 1) {
    if (ranges[i].start < ranges[i - 1].end) {
      return true;
    }
  }
  return false;
}

export function resolveMembershipDiscount(
  membership: MembershipDiscountInput | undefined | null,
  productId?: string,
  now: Date = new Date(),
): MembershipDiscountResolution {
  const basePercent = membership?.discountPercent || 0;
  const special = productId ? membership?.benefits?.specialOfferProducts?.[productId] : undefined;
  const specialPercent =
    special?.discountPercent != null && special.discountPercent > 0 ? special.discountPercent : undefined;

  const activePeriods = (special?.periodOffers || []).filter((offer) => isPeriodOfferActive(offer, now));
  if (activePeriods.length > 0) {
    const period = activePeriods.reduce((best, current) =>
      (current.discountPercent || 0) > (best.discountPercent || 0) ? current : best,
    );
    return {
      percent: period.discountPercent || 0,
      source: 'period',
      basePercent,
      specialPercent,
      period,
    };
  }

  if (specialPercent != null) {
    return {
      percent: specialPercent,
      source: 'special',
      basePercent,
      specialPercent,
    };
  }

  if (basePercent > 0) {
    return {
      percent: basePercent,
      source: 'base',
      basePercent,
    };
  }

  return {
    percent: 0,
    source: 'none',
    basePercent,
  };
}

export function getStatementMembershipDiscountPercent(statement?: {
  payment?: { membershipDiscountDetail?: { percent?: number } };
  buyer?: { membership?: { discountPercent?: number } };
}): number {
  const detailPercent = statement?.payment?.membershipDiscountDetail?.percent;
  if (detailPercent != null) {
    return detailPercent;
  }
  return statement?.buyer?.membership?.discountPercent || 0;
}

export function formatMembershipDiscountLabel(detail?: MembershipDiscountDetail | null): string {
  if (!detail || !detail.percent) {
    return '';
  }
  if (detail.source === 'period') {
    return `기간 ${detail.percent}%${detail.basePercent ? `, 기본 ${detail.basePercent}%` : ''}`;
  }
  if (detail.source === 'special') {
    return `특별 ${detail.percent}%`;
  }
  return `${detail.percent}%`;
}

function formatDateTime(value?: Date | string) {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

export function formatMembershipDiscountSummary(detail?: MembershipDiscountDetail | null): string {
  const rate = formatMembershipDiscountLabel(detail);
  if (!rate) {
    return '';
  }
  const name = detail?.name || detail?.alias || '';
  const period =
    detail?.source === 'period' && detail.period?.startDate && detail.period?.endDate
      ? `${formatDateTime(detail.period.startDate)} ~ ${formatDateTime(detail.period.endDate)}`
      : '';
  return [name, rate, period].filter(Boolean).join(' · ');
}
